import { useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/app-store';
import type { MissileTrajectory } from '@/types';

// ---------------------------------------------------------------------------
// 100ms animation loop for simulation missile trajectories
// Uses startedAt + flightTimeMs to compute real-time progress
// ---------------------------------------------------------------------------

function interpolate(
  fromLon: number, fromLat: number,
  toLon: number, toLat: number,
  progress: number,
): [number, number] {
  return [
    fromLon + (toLon - fromLon) * progress,
    fromLat + (toLat - fromLat) * progress,
  ];
}

function tickSimMissiles(missiles: MissileTrajectory[], nowMs: number): MissileTrajectory[] {
  return missiles.map((m): MissileTrajectory => {
    const elapsed = nowMs - m.startedAt;
    if (elapsed < 0) return m;

    if (elapsed < m.flightTimeMs) {
      const progress = elapsed / m.flightTimeMs;
      const [currentLon, currentLat] = interpolate(
        m.originLon, m.originLat, m.targetLon, m.targetLat, progress,
      );
      return { ...m, status: 'flying', progress, currentLon, currentLat };
    } else if (elapsed < m.flightTimeMs + 3000) {
      // Impact flash for 3s
      return {
        ...m, status: 'impact', progress: 1,
        currentLon: m.targetLon, currentLat: m.targetLat,
      };
    }
    // Done — keep at target position but mark cooldown so it's filtered out
    return { ...m, status: 'cooldown', progress: 1 };
  });
}

export function useSimMissileAnimation() {
  const simMissiles = useAppStore((s) => s.simMissiles);
  const setSimMissiles = useAppStore((s) => s.setSimMissiles);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const missileRef = useRef(simMissiles);
  missileRef.current = simMissiles;

  useEffect(() => {
    if (simMissiles.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setSimMissiles(tickSimMissiles(missileRef.current, Date.now()));
    }, 100);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simMissiles.length, setSimMissiles]);
}
